import type { StrokePoint } from "../canvas/canvasTypes";

export interface ActiveStrokeLimits {
  maxPoints: number;
  maxEstimatedBytes: number;
  compactAtRatio: number;
  compactToRatio: number;
  minimumDistance: number;
  maximumSegmentLength: number;
  cornerAngleRadians: number;
  deviationTolerance: number;
  pressureThreshold: number;
}

export interface ActiveStrokeCaptureState {
  pointCount: number;
  estimatedBytes: number;
  effectivePointLimit: number;
  compactionCount: number;
}

export const ACTIVE_STROKE_BASE_BYTES = 64;
export const ACTIVE_STROKE_POINT_BYTES = 24;

export const DEFAULT_ACTIVE_STROKE_LIMITS: Readonly<ActiveStrokeLimits> = {
  maxPoints: 4_096,
  maxEstimatedBytes: 128 * 1_024,
  compactAtRatio: 0.75,
  compactToRatio: 0.5,
  minimumDistance: 0.65,
  maximumSegmentLength: 10,
  cornerAngleRadians: Math.PI / 16,
  deviationTolerance: 0.75,
  pressureThreshold: 0.04,
};

function copyPoint(point: Readonly<StrokePoint>): StrokePoint {
  return { x: point.x, y: point.y, pressure: point.pressure };
}

function pointsEqual(left: Readonly<StrokePoint>, right: Readonly<StrokePoint>): boolean {
  return left.x === right.x && left.y === right.y && left.pressure === right.pressure;
}

function pointDistance(left: Readonly<StrokePoint>, right: Readonly<StrokePoint>): number {
  return Math.hypot(right.x - left.x, right.y - left.y);
}

function turnAngle(
  previous: Readonly<StrokePoint>,
  current: Readonly<StrokePoint>,
  next: Readonly<StrokePoint>,
): number {
  const incomingX = current.x - previous.x;
  const incomingY = current.y - previous.y;
  const outgoingX = next.x - current.x;
  const outgoingY = next.y - current.y;
  const incomingLength = Math.hypot(incomingX, incomingY);
  const outgoingLength = Math.hypot(outgoingX, outgoingY);
  if (incomingLength === 0 || outgoingLength === 0) return 0;
  const cosine = Math.min(
    1,
    Math.max(
      -1,
      (incomingX * outgoingX + incomingY * outgoingY) / (incomingLength * outgoingLength),
    ),
  );
  return Math.acos(cosine);
}

function distanceFromSegment(
  point: Readonly<StrokePoint>,
  start: Readonly<StrokePoint>,
  end: Readonly<StrokePoint>,
): number {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const lengthSquared = deltaX * deltaX + deltaY * deltaY;
  if (lengthSquared === 0) return pointDistance(point, start);
  const projection = Math.min(
    1,
    Math.max(0, ((point.x - start.x) * deltaX + (point.y - start.y) * deltaY) / lengthSquared),
  );
  return Math.hypot(
    point.x - (start.x + projection * deltaX),
    point.y - (start.y + projection * deltaY),
  );
}

function pointImportance(
  previous: Readonly<StrokePoint>,
  current: Readonly<StrokePoint>,
  next: Readonly<StrokePoint>,
): number {
  const pressureStep = Math.max(
    Math.abs(current.pressure - previous.pressure),
    Math.abs(next.pressure - current.pressure),
  );
  const pressurePeak = Math.abs(current.pressure - (previous.pressure + next.pressure) / 2);
  return (
    distanceFromSegment(current, previous, next) * 4 +
    (turnAngle(previous, current, next) / Math.PI) * 20 +
    pressureStep * 20 +
    pressurePeak * 40
  );
}

export function compactStrokePoints(
  points: ReadonlyArray<Readonly<StrokePoint>>,
  targetCount: number,
): StrokePoint[] {
  if (points.length <= targetCount) return points.map(copyPoint);
  if (targetCount < 2) throw new Error("Stroke compaction must preserve two endpoints.");

  const ranked = points
    .slice(1, -1)
    .map((point, offset) => {
      const index = offset + 1;
      const previous = points[index - 1];
      const next = points[index + 1];
      if (!previous || !next) throw new Error("Stroke compaction received invalid points.");
      return { index, score: pointImportance(previous, point, next) };
    })
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, targetCount - 2)
    .map((item) => item.index)
    .sort((left, right) => left - right);

  const first = points[0];
  const last = points.at(-1);
  if (!first || !last) return [];
  return [copyPoint(first), ...ranked.map((index) => copyPoint(points[index]!)), copyPoint(last)];
}

export class ActiveStrokeCapture {
  private readonly effectivePointLimit: number;
  private confirmed: StrokePoint[] = [];
  private candidate: StrokePoint | null = null;
  private latest: StrokePoint | null = null;
  private compactionCount = 0;

  public constructor(
    private readonly limits: Readonly<ActiveStrokeLimits> = DEFAULT_ACTIVE_STROKE_LIMITS,
  ) {
    const memoryPointLimit = Math.floor(
      (limits.maxEstimatedBytes - ACTIVE_STROKE_BASE_BYTES) / ACTIVE_STROKE_POINT_BYTES,
    );
    this.effectivePointLimit = Math.min(limits.maxPoints, memoryPointLimit);
    if (
      this.effectivePointLimit < 2 ||
      limits.compactAtRatio <= 0 ||
      limits.compactAtRatio > 1 ||
      limits.compactToRatio <= 0 ||
      limits.compactToRatio >= limits.compactAtRatio
    ) {
      throw new Error("Active stroke limits are invalid.");
    }
  }

  public begin(point: Readonly<StrokePoint>): void {
    const first = copyPoint(point);
    this.confirmed = [first];
    this.candidate = null;
    this.latest = first;
    this.compactionCount = 0;
  }

  public add(point: Readonly<StrokePoint>): void {
    if (this.confirmed.length === 0) {
      this.begin(point);
      return;
    }

    const next = copyPoint(point);
    this.latest = next;
    const anchor = this.confirmed.at(-1)!;
    if (!this.candidate) {
      if (
        pointDistance(anchor, next) >= this.limits.minimumDistance ||
        Math.abs(next.pressure - anchor.pressure) >= this.limits.pressureThreshold
      ) {
        this.candidate = next;
      }
      return;
    }

    if (this.shouldPromoteCandidate(anchor, this.candidate, next)) {
      this.confirmed.push(this.candidate);
      this.candidate = next;
      this.compactWhenApproachingLimit();
    } else {
      this.candidate = next;
    }
  }

  public finish(): StrokePoint[] {
    if (this.confirmed.length === 0) return [];
    const result = this.confirmed.map(copyPoint);
    const tail = this.candidate ?? this.latest;
    if (tail && !pointsEqual(result.at(-1)!, tail)) result.push(copyPoint(tail));
    return result.length > this.effectivePointLimit
      ? compactStrokePoints(result, this.effectivePointLimit)
      : result;
  }

  public reset(): void {
    this.confirmed = [];
    this.candidate = null;
    this.latest = null;
    this.compactionCount = 0;
  }

  public getState(): ActiveStrokeCaptureState {
    const pointCount = this.finish().length;
    return {
      pointCount,
      estimatedBytes: ACTIVE_STROKE_BASE_BYTES + pointCount * ACTIVE_STROKE_POINT_BYTES,
      effectivePointLimit: this.effectivePointLimit,
      compactionCount: this.compactionCount,
    };
  }

  private shouldPromoteCandidate(
    anchor: Readonly<StrokePoint>,
    candidate: Readonly<StrokePoint>,
    next: Readonly<StrokePoint>,
  ): boolean {
    const pressureChanged =
      Math.abs(candidate.pressure - anchor.pressure) >= this.limits.pressureThreshold ||
      Math.abs(next.pressure - candidate.pressure) >= this.limits.pressureThreshold;
    if (pressureChanged) return true;
    if (pointDistance(anchor, candidate) >= this.limits.maximumSegmentLength) return true;
    if (turnAngle(anchor, candidate, next) >= this.limits.cornerAngleRadians) return true;
    return distanceFromSegment(candidate, anchor, next) >= this.limits.deviationTolerance;
  }

  private compactWhenApproachingLimit(): void {
    const triggerCount = Math.max(
      3,
      Math.floor(this.effectivePointLimit * this.limits.compactAtRatio),
    );
    if (this.confirmed.length + 1 < triggerCount) return;
    const targetCount = Math.max(
      2,
      Math.floor(this.effectivePointLimit * this.limits.compactToRatio),
    );
    this.confirmed = compactStrokePoints(this.confirmed, targetCount);
    this.compactionCount += 1;
  }
}
